import { Link, Navigate, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink, ListChecks, MoreHorizontal, UserMinus } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import {
  INTERVIEW_STAGES,
  JOB_STATUS_LABELS,
  STAGE_BAR_STAGES,
  isStageBarKey,
  stageBarLanding,
  type JobBuilderSection,
  type JobDetail,
  type JobOpeningStatus,
  type ModuleKey,
  type ModuleState,
  type StageBar as StageBarData,
  type StageBarKey,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { MenuItem, Popover, usePopover } from '../../../components/Popover';
import { Skeleton, SkeletonLines } from '../../../components/Skeleton';
import { StageBar } from '../../../components/StageBar';
import { JOB_STATUS_TONES } from '../../../components/status-tones';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ToneChip, toneClass } from '../../../components/Tone';
import { registerCommands } from '../../../shell/command-registry';
import { useRoom } from '../../../shell/SocketProvider';
import { useCanView } from '../../../stores/session';
import { useJob, useStageBar } from '../api/jobs-api';
import { ResultsTab } from '../../assessments/components/ResultsTab';
import { CompareTab } from '../../comparison/components/CompareTab';
import { SelectedStage } from '../../comparison/components/SelectedStage';
import { DecisionLogTab } from '../../decisions/components/DecisionLogTab';
import { OffersTab } from '../../offers/components/OffersTab';
import { PipelineTab } from '../../pipeline/pages/PipelineTab';
import type { PipelineSearch } from '../../pipeline/pipeline-search';
import { PoolMatchTab } from '../../pools/components/PoolMatchTab';
import { Timeline } from '../../communication/components/Timeline';
import { SortingEntry } from '../../sorting/components/SortingEntry';
import { ChangesPanel } from '../components/hub/ChangesPanel';
import { JobStatusActions } from '../components/hub/JobStatusActions';
import { JobSummary } from '../components/hub/JobSummary';
import { OutPanel } from '../components/hub/OutPanel';
import { OverviewTab } from '../components/hub/OverviewTab';
import { InterviewMatrix } from '../components/hub/InterviewMatrix';
import { RoundSetup } from '../components/hub/RoundSetup';
import { JobBuilder } from '../components/builder/JobBuilder';
import { ImportStage } from '../../imports/components/ImportStage';
import { RankingStage } from '../../ranking/components/RankingStage';
import { ContactStage } from '../../contact/components/ContactStage';
import { PhoneScreenStage } from '../../phone-screen/components/PhoneScreenStage';
import { ReviewStage } from '../../shortlist/components/ReviewStage';
import { ShortlistStage } from '../../shortlist/components/ShortlistStage';
import styles from './JobHubPage.module.css';
import fit from '../../../components/FitPage.module.css';
import { goneId, useRecordVisit } from '../../../lib/use-record-visit';
import { useModule, useModules } from '../../../lib/modules';

/**
 * JOB-12 (R2, §L33, QoL §15) — one job, in stage order. The stage bar replaces JOB-10's tab
 * strip, and the tabs live on as each stage's content. Overview, Activity and the parts that
 * aren't stages sit in the header's ⋯ menu. Every stage and every menu entry has its own
 * address, so an old notification link still lands where it always did (non-negotiable #3).
 */

/** JOB-10's old tabs that aren't stages: reached from the ⋯ menu, and by their old addresses. */
export const JOB_HUB_PANELS = ['overview', 'pipeline', 'postings', 'compare', 'offers', 'decisions', 'pool', 'activity', 'changes'] as const;
export type JobHubPanel = (typeof JOB_HUB_PANELS)[number];

export type JobPageView =
  /** `/jobs/{id}`: the job's current stage (DECISIONS-LOG 2026-10-12 Q2). */
  | { readonly kind: 'landing' }
  /** `section` picks JOB-13's section on stage ① Job (`/jobs/{id}/setup/{section}`, R3). */
  | { readonly kind: 'stage'; readonly stage: StageBarKey; readonly section?: JobBuilderSection }
  | { readonly kind: 'panel'; readonly panel: JobHubPanel };

const BUILT_STAGES: readonly StageBarKey[] = STAGE_BAR_STAGES.filter((stage) => stage.isBuilt).map((stage) => stage.key);

/**
 * The `/jobs/{id}/{segment}` address as a view. `settings` was JOB-11: stage ① Job is the
 * setup now. A stage with no screen yet (Import before R4…) opens the current stage instead.
 */
export function jobPageViewOf(segment: string): JobPageView {
  if (segment === 'settings' || segment === 'setup') return { kind: 'stage', stage: 'job' };
  if (isStageBarKey(segment) && BUILT_STAGES.includes(segment)) return { kind: 'stage', stage: segment };
  if (isJobHubPanel(segment)) return { kind: 'panel', panel: segment };
  return { kind: 'landing' };
}

function isJobHubPanel(value: string): value is JobHubPanel {
  return (JOB_HUB_PANELS as readonly string[]).includes(value);
}

const PANEL_LABELS: Readonly<Record<JobHubPanel, string>> = {
  overview: 'Overview',
  pipeline: 'All candidates',
  postings: 'Form links',
  compare: 'Compare',
  offers: 'Offers',
  decisions: 'Decisions',
  pool: 'Talent pool',
  activity: 'Activity',
  changes: 'Changes',
};

/** R1 (§L34): the panels that belong to a switchable module. */
const PANEL_MODULES: Partial<Record<JobHubPanel, ModuleKey>> = {
  postings: 'career_site',
  compare: 'compare',
  offers: 'offers',
  pool: 'pools',
  // ADR 0088: what the requester wrote is Hire requests' (§L34).
  changes: 'requisitions',
};

/** R1 (§L34): a stage that belongs to a switchable module leaves the bar while it's off. */
const STAGE_MODULES: Partial<Record<StageBarKey, ModuleKey>> = {
  assessment: 'assessments',
};

/** §L33: which application stages each bar stage lists. Selected is `offer` and later. */
const STAGE_FILTERS: Partial<Record<StageBarKey, string>> = {
  assessment: 'assessment',
  interview_1: 'interview_1',
  interview_2: 'interview_2',
  interview_3: 'interview_3',
  selected: 'offer,hired_pending_join,joined',
};

interface JobHubPageProps {
  readonly jobId: string;
  readonly view: JobPageView;
  /** The list's filters, read from the address (06-qol §6). */
  readonly search: PipelineSearch;
  readonly onSearch: (next: PipelineSearch) => void;
}

export function JobHubPage({ jobId, view: addressed, search, onSearch }: JobHubPageProps): ReactNode {
  const job = useJob(jobId);
  const bar = useStageBar(jobId);
  const navigate = useNavigate();
  const modules = useModules();
  const [isOutOpen, setIsOutOpen] = useState(false);
  useRecordVisit(job.data ? { kind: 'job', id: jobId, title: job.data.title, subtitle: job.data.reference, path: `/jobs/${jobId}` } : null, goneId(job.error, jobId));
  // §L33: the counts move when anyone moves a candidate on this job.
  useRoom(`job:${jobId}`);

  if (job.isPending) return <HubSkeleton />;
  if (job.error) {
    if (job.error.status === 403) return <ForbiddenState resource="this job" contact="Your admin" />;
    if (job.error.status === 404) return <NotFoundState />;
    return <ServerErrorState onRetry={() => void job.refetch()} />;
  }

  const detail = job.data;
  const view = shownViewOf(addressed, modules);
  const shownBar = bar.data ? withoutSwitchedOff(bar.data, modules) : undefined;
  const activeStage = activeStageOf(view, shownBar);
  // A stage whose main content is a table fits the screen and only the table scrolls (owner, 2026-10-25).
  const isTableStage = view.kind === 'panel' ? view.panel === 'pipeline' : activeStage !== null && !PAGE_SCROLL_STAGES.has(activeStage);
  // A stage swap is a component swap, never a page load (§5a rule 5).
  const select = (key: StageBarKey): void => void navigate({ to: '/jobs/$jobId/$tab', params: { jobId, tab: key } });

  return (
    <div className={isTableStage ? `${styles.page} ${fit.fit}` : styles.page}>
      <HubHeader detail={detail} activeStage={activeStage} />
      <JobStageCommands detail={detail} stageKeys={shownBar?.stages.map((stage) => stage.key).join(',') ?? ''} onOut={setIsOutOpen} />
      <div className={styles.barRow}>
        {shownBar ? (
          <StageBar
            stages={shownBar.stages}
            setupMissing={shownBar.setupMissing}
            outTotal={shownBar.out.total}
            activeKey={activeStage}
            hrefOf={(key) => `/jobs/${jobId}/${key}`}
            onSelect={select}
            onOut={() => setIsOutOpen(true)}
          />
        ) : (
          !bar.error && <Skeleton shape="title" width="70%" />
        )}
      </div>
      <div className={isTableStage ? `${styles.content} ${fit.fill}` : styles.content} role="tabpanel" aria-label={contentLabel(view, shownBar, activeStage)}>
        {view.kind === 'panel' ? (
          <PanelContent detail={detail} panel={view.panel} search={search} onSearch={onSearch} />
        ) : activeStage ? (
          <StageContent key={activeStage} detail={detail} stage={activeStage} section={view.kind === 'stage' ? (view.section ?? null) : null} search={search} onSearch={onSearch} />
        ) : bar.error ? (
          <ServerErrorState onRetry={() => void bar.refetch()} />
        ) : (
          <SkeletonLines count={6} />
        )}
      </div>
      {shownBar && <OutPanel jobId={jobId} out={shownBar.out} isOpen={isOutOpen} onClose={() => setIsOutOpen(false)} />}
    </div>
  );
}

/**
 * Stages that are not mainly a table: a builder, an import, the review board, results, or Selected with its offers.
 * Ranking too, for now: the Ask Jev panel and filters above its table leave the table no room on a laptop.
 */
const PAGE_SCROLL_STAGES: ReadonlySet<StageBarKey> = new Set<StageBarKey>(['job', 'import', 'ranking', 'review', 'assessment', 'selected']);

/**
 * #13 (§L28, §L34): the address of a stage or a ⋯ panel whose module is switched off opens the
 * job's landing instead — an old link to `/jobs/{id}/assessment` never shows the results.
 */
export function shownViewOf(view: JobPageView, modules: ModuleState): JobPageView {
  const owner = moduleOfView(view);
  return owner === undefined || modules[owner] ? view : { kind: 'landing' };
}

function moduleOfView(view: JobPageView): ModuleKey | undefined {
  if (view.kind === 'stage') return STAGE_MODULES[view.stage];
  if (view.kind === 'panel') return PANEL_MODULES[view.panel];
  return undefined;
}

function withoutSwitchedOff(bar: StageBarData, modules: Readonly<Record<ModuleKey, boolean>>): StageBarData {
  const stages = bar.stages.filter((stage) => {
    const owner = STAGE_MODULES[stage.key];
    return owner === undefined || modules[owner];
  });
  return { ...bar, stages };
}

/** The stage on screen: the one in the address, else the job's current stage once the bar is here. */
function activeStageOf(view: JobPageView, bar: StageBarData | undefined): StageBarKey | null {
  if (view.kind === 'stage') return view.stage;
  if (view.kind === 'panel' || !bar) return null;
  return stageBarLanding(bar);
}

function contentLabel(view: JobPageView, bar: StageBarData | undefined, stage: StageBarKey | null): string {
  if (view.kind === 'panel') return PANEL_LABELS[view.panel];
  return bar?.stages.find((candidate) => candidate.key === stage)?.label ?? 'Stage';
}

/** 06-qol §1: while a job is open, each of its stages is one palette entry away. */
function JobStageCommands({ detail, stageKeys, onOut }: { readonly detail: JobDetail; readonly stageKeys: string; readonly onOut: (isOpen: boolean) => void }): null {
  const navigate = useNavigate();
  const jobId = detail.id;
  const title = detail.publicTitle ?? detail.title;
  useEffect(() => {
    const stages = STAGE_BAR_STAGES.filter((stage) => stageKeys.split(',').includes(stage.key));
    return registerCommands([
      ...stages.map((stage) => ({
        id: `job.stage.${jobId}.${stage.key}`,
        label: `Go to ${stage.label.toLowerCase()}`,
        section: 'action' as const,
        context: title,
        permission: 'job_opening' as const,
        icon: ListChecks,
        keywords: ['stage', 'bar'],
        run: () => void navigate({ to: '/jobs/$jobId/$tab', params: { jobId, tab: stage.key } }),
      })),
      { id: `job.out.${jobId}`, label: 'Show who is out of this job', section: 'action', context: title, permission: 'application', icon: UserMinus, keywords: ['rejected', 'withdrawn', 'closed', 'out'], run: () => onOut(true) },
    ]);
  }, [jobId, title, stageKeys, navigate, onOut]);
  return null;
}

interface ContentProps {
  readonly detail: JobDetail;
  readonly search: PipelineSearch;
  readonly onSearch: (next: PipelineSearch) => void;
}

function StageContent({ detail, stage, section, search, onSearch }: ContentProps & { readonly stage: StageBarKey; readonly section: JobBuilderSection | null }): ReactNode {
  const canSeeResults = useCanView('assessment_attempt');
  const canSeeOffers = useCanView('offer');
  const canSeeSelected = useCanView('stage_decision');
  const isOffersOn = useModule('offers');
  // R3: stage ① Job is the one-page job builder (JOB-13).
  if (stage === 'job') return <JobBuilder detail={detail} section={section} />;
  // R4: stage ② Import is IMP-01 and IMP-02; `?import=` picks one (N-86 opens it).
  if (stage === 'import') return <ImportStage detail={detail} importId={search.import ?? null} onOpen={(importId) => onSearch(withImport(search, importId))} />;
  // R5: stage ③ Ranking is RNK-01, with RNK-02 as its side sheet (`?app=`); N-87 opens `?tab=top_fit`.
  if (stage === 'ranking') return <RankingStage detail={detail} search={search} onSearch={onSearch} />;
  // R6: ④ Review is "Start review" and the conveyor (SCR-03); ⑤ Shortlist is SHL-02 (N-91 opens it);
  // ⑥ Contact is a plain list until R7's tracker (DECISIONS-LOG "Start R6" Q2).
  if (stage === 'review') return <ReviewStage detail={detail} search={search} onSearch={onSearch} />;
  if (stage === 'shortlist') return <ShortlistStage detail={detail} search={search} onSearch={onSearch} />;
  // ADR 0079: the optional phone Screening step (PHS-01), between Shortlist and Contact.
  if (stage === 'phone_screen') return <PhoneScreenStage detail={detail} search={search} onSearch={onSearch} />;
  if (stage === 'contact') return <ContactStage detail={detail} search={search} onSearch={onSearch} />;
  if (stage === 'assessment' && canSeeResults) return <ResultsTab detail={detail} />;
  // R8 (F24.11): ✓ Selected lists who was chosen, when, by whom and from which round; the offers
  // follow underneath only while Offers is on (§L33).
  if (stage === 'selected' && canSeeSelected) {
    return (
      <div className={styles.stageContent}>
        <SelectedStage detail={detail} />
        {isOffersOn && canSeeOffers && <OffersTab detail={detail} />}
      </div>
    );
  }
  const lockedStage = STAGE_FILTERS[stage] ?? 'screening';
  // C5 (ADR 0077): each round is set up in its own Interview step, once the job is open.
  const interviewStage = INTERVIEW_STAGES.find((candidate) => candidate === stage);
  return (
    <div className={`${styles.stageContent} ${fit.fill}`}>
      {/* Owner 2026-11-01: the scoring matrix on top, as the panel's guide; it folds away. */}
      {interviewStage && <InterviewMatrix key={detail.id} job={detail} />}
      {interviewStage && <RoundSetup key={`${detail.id}:${interviewStage}`} job={detail} stage={interviewStage} />}
      {interviewStage ? (
        <div className={styles.roundList}>
          <PipelineTab detail={detail} search={search} onSearch={onSearch} lockedStage={lockedStage} />
        </div>
      ) : (
        <PipelineTab detail={detail} search={search} onSearch={onSearch} lockedStage={lockedStage} />
      )}
      {stage === 'selected' && isOffersOn && canSeeOffers && <OffersTab detail={detail} />}
    </div>
  );
}

function withImport(search: PipelineSearch, importId: string | null): PipelineSearch {
  const { import: _current, ...rest } = search;
  return importId ? { ...rest, import: importId } : rest;
}

function PanelContent({ detail, panel, search, onSearch }: ContentProps & { readonly panel: JobHubPanel }): ReactNode {
  return (
    <section className={panel === 'pipeline' ? `${styles.panel} ${fit.fill}` : styles.panel} aria-label={PANEL_LABELS[panel]}>
      <h2 className={styles.panelTitle}>{PANEL_LABELS[panel]}</h2>
      <Panel detail={detail} panel={panel} search={search} onSearch={onSearch} />
    </section>
  );
}

function Panel({ detail, panel, search, onSearch }: ContentProps & { readonly panel: JobHubPanel }): ReactNode {
  switch (panel) {
    case 'overview':
      return <OverviewTab detail={detail} />;
    case 'pipeline':
      return <PipelineTab detail={detail} search={search} onSearch={onSearch} />;
    case 'postings':
      // Owner 2026-11-01: Form links moved under Application form in the job setup; an old link or bookmark lands there.
      return <Navigate to="/jobs/$jobId/setup/$section" params={{ jobId: detail.id, section: 'form' }} replace />;
    case 'compare':
      return <CompareTab detail={detail} />;
    case 'offers':
      return <OffersTab detail={detail} />;
    case 'decisions':
      return <DecisionLogTab detail={detail} />;
    case 'pool':
      return <PoolMatchTab detail={detail} />;
    case 'activity':
      return <Timeline subject={{ kind: 'job', id: detail.id }} title="Activity on this job" />;
    case 'changes':
      return <ChangesPanel detail={detail} />;
  }
}

/** The header's ⋯ menu: what used to be tabs and isn't a stage, gated like the tabs were (R1). */
/** Panels kept at their address but not listed in the job's More menu. */
const OFF_MENU_PANELS: readonly JobHubPanel[] = ['postings', 'pipeline'];

function MoreMenu({ detail }: { readonly detail: JobDetail }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const modules = useModules();
  const canSee: Readonly<Record<JobHubPanel, boolean>> = {
    overview: true,
    pipeline: useCanView('application'),
    activity: true,
    changes: true,
    postings: useCanView('posting'),
    compare: useCanView('stage_decision'),
    decisions: useCanView('stage_decision'),
    offers: useCanView('offer'),
    pool: useCanView('talent_pool'),
  };
  // Owner 2026-11-01: Form links is not a tab any more; it sits under Application form in the job setup. Evening review
  // 2026-11-01 item 13: All candidates isn't a step of the hiring flow either; it is the top bar's Candidates button now.
  const panels = JOB_HUB_PANELS.filter((panel) => {
    const owner = PANEL_MODULES[panel];
    return !OFF_MENU_PANELS.includes(panel) && canSee[panel] && (owner === undefined || modules[owner]);
  });
  return (
    <>
      <Button ref={triggerRef} variant="outline" icon={MoreHorizontal} onClick={toggle} aria-haspopup="menu" aria-label="More about this job">
        More
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" label="More about this job">
        {panels.map((panel) => (
          <MenuItem key={panel} href={`/jobs/${detail.id}/${panel}`} onClick={close}>
            {PANEL_LABELS[panel]}
          </MenuItem>
        ))}
      </Popover>
    </>
  );
}

/** DECISIONS-LOG 2026-10-19 Q7: the header's "Start review" only on ③ Ranking and ④ Review, never on a draft. */
const START_REVIEW_STAGES: readonly StageBarKey[] = ['ranking', 'review'];

export function isStartReviewShown(status: JobOpeningStatus, stage: StageBarKey | null): boolean {
  return status !== 'draft' && stage !== null && START_REVIEW_STAGES.includes(stage);
}

function HubHeader({ detail, activeStage }: { readonly detail: JobDetail; readonly activeStage: StageBarKey | null }): ReactNode {
  const isOnCareerSite = useModule('career_site') && detail.status === 'open' && !detail.isConfidential;
  return (
    <header className={styles.header}>
      <Link to="/jobs" className={styles.back}>
        <ArrowLeft size={14} aria-hidden />
        Jobs
      </Link>
      <div className={styles.titleRow}>
        <div className={styles.titleBlock}>
          <div className={styles.titleLine}>
            <h1 className={styles.title}>{detail.publicTitle ?? detail.title}</h1>
            <ToneChip tone={JOB_STATUS_TONES[detail.status]}>{JOB_STATUS_LABELS[detail.status]}</ToneChip>
            {detail.isConfidential && (
              <Chip tone="attention" plain>
                Confidential
              </Chip>
            )}
          </div>
          <p className={styles.meta}>
            {[
              detail.reference,
              detail.departmentName,
              detail.ventureName,
              detail.recruiterName ? `Recruiter ${detail.recruiterName}` : null,
              detail.hiringManagerName ? `Hiring manager ${detail.hiringManagerName}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <JobSummary detail={detail} />
        </div>
        <div className={styles.headerActions}>
          {isOnCareerSite && (
            <a className={styles.externalLink} href={detail.careerSiteUrl} target="_blank" rel="noreferrer">
              Career site
              <ExternalLink size={14} aria-hidden />
            </a>
          )}
          {isStartReviewShown(detail.status, activeStage) && <SortingEntry jobId={detail.id} jobTitle={detail.publicTitle ?? detail.title} />}
          <JobStatusActions detail={detail} />
          <MoreMenu detail={detail} />
        </div>
      </div>
      {/* Evening review 2026-11-01 item 11: the demo stopped at Select on a draft job. Say so before anyone gets that far. */}
      {detail.status === 'draft' && detail.hasCandidates && (
        <p className={`${toneClass} ${styles.reason}`} data-tone={JOB_STATUS_TONES.draft} role="note">
          <strong>Still a draft.</strong> You can import, review and interview people, but nobody can be selected until the job is opened.
          {detail.actions.includes('open') ? (
            <>
              {' '}
              <Link to="/jobs/$jobId/$tab" params={{ jobId: detail.id, tab: 'job' }}>
                Open it from ① Job
              </Link>
              .
            </>
          ) : (
            ' Ask the job’s recruiter or HR to open it.'
          )}
        </p>
      )}
      {detail.statusReason && detail.status !== 'open' && (
        <p className={`${toneClass} ${styles.reason}`} data-tone={JOB_STATUS_TONES[detail.status]}>
          <strong>{reasonLead(detail.status)}</strong> {detail.statusReason}
        </p>
      )}
    </header>
  );
}

function reasonLead(status: JobOpeningStatus): string {
  if (status === 'on_hold') return 'On hold:';
  if (status === 'cancelled') return 'Cancelled:';
  if (status === 'reopened') return 'Reopened:';
  return 'Closed:';
}

function HubSkeleton(): ReactNode {
  return (
    <div className={styles.page}>
      <Skeleton shape="title" width="40%" />
      <SkeletonLines count={2} />
      <SkeletonLines count={6} />
    </div>
  );
}
